import { useEffect } from "react";
import { useAuth } from "@/lib/auth-context";

const SENT = new Set<string>();

/** Posts a failure to the owner-alert endpoint. Never throws. */
export function reportFailure(input: {
  summary: string;
  details?: string;
  source?: string;
  userEmail?: string;
}) {
  if (typeof window === "undefined") return;
  const path = window.location.pathname;
  const key = `${input.summary}|${path}`;
  if (SENT.has(key)) return; // don't re-mail the same failure this session
  SENT.add(key);

  void fetch("/api/public/error-report", {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({
      summary: input.summary.slice(0, 300),
      details: input.details?.slice(0, 2000),
      path,
      source: input.source ?? "browser",
      userEmail: input.userEmail,
    }),
    keepalive: true,
  }).catch(() => {});
}

/**
 * Global crash reporter: any uncaught error, rejected promise or resource
 * failure in the app emails the site owner.
 */
export function ErrorReporter() {
  const { user } = useAuth();

  useEffect(() => {
    const email = user?.email ?? undefined;

    const onError = (event: ErrorEvent) => {
      const err = event.error as Error | undefined;
      reportFailure({
        summary: err?.message || event.message || "Uncaught error",
        details: err?.stack || `${event.filename}:${event.lineno}:${event.colno}`,
        userEmail: email,
      });
    };

    const onRejection = (event: PromiseRejectionEvent) => {
      const reason = event.reason as any;
      reportFailure({
        summary:
          (typeof reason === "string" ? reason : reason?.message) || "Unhandled promise rejection",
        details: reason?.stack ?? undefined,
        source: "browser-promise",
        userEmail: email,
      });
    };

    window.addEventListener("error", onError);
    window.addEventListener("unhandledrejection", onRejection);
    return () => {
      window.removeEventListener("error", onError);
      window.removeEventListener("unhandledrejection", onRejection);
    };
  }, [user]);

  return null;
}
